import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Download, AlertTriangle, CheckCircle2, ShieldAlert, Info, Copy, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import ShareBlock from "@/components/ShareBlock";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VERDICT_COLOURS = {
  FAIR:               { bg: "bg-[#064E2E]", label: "FAIR" },
  SOMEWHAT_HIGH:      { bg: "bg-[#B45309]", label: "SOMEWHAT INFLATED" },
  INFLATED:           { bg: "bg-[#B91C1C]", label: "SIGNIFICANTLY INFLATED" },
  UNNECESSARY:        { bg: "bg-[#B91C1C]", label: "WORK NOT REQUIRED" },
  INSURANCE_RELEVANT: { bg: "bg-[#0F766E]", label: "MAY BE INSURANCE-RELEVANT" },
  INCONCLUSIVE:       { bg: "bg-[#475569]", label: "INCONCLUSIVE — FURTHER REVIEW NEEDED" },
};

const RISK_COLOUR = {
  red:   "bg-[#B91C1C]",
  amber: "bg-[#B45309]",
  green: "bg-[#064E2E]",
};

export default function ReportViewer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND}/api/reports/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  function copyLink() {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  }

  if (loading) {
    return (
      <div className="section-pad container-phs flex items-center gap-2 text-[#475569]" data-testid="report-loading">
        <Loader2 className="animate-spin" size={18}/> Loading your report…
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="section-pad container-phs max-w-xl text-center" data-testid="report-not-found">
        <AlertTriangle className="mx-auto text-[#B88A2A]" size={36}/>
        <h1 className="font-display text-3xl mt-4 text-[#071A33]">Report not found</h1>
        <p className="mt-2 text-[#475569]">This link is invalid or the report has been withdrawn. Please check the link in your email.</p>
        <Link to="/contact" className="btn-primary mt-6 inline-flex">Contact us <ArrowRight size={16}/></Link>
      </div>
    );
  }

  const { case: c, report: r } = data;
  const vs = VERDICT_COLOURS[r.verdict] || VERDICT_COLOURS.INCONCLUSIVE;
  const pdfUrl = `${BACKEND}/api/reports/${token}/pdf`;

  return (
    <div className="bg-[#F7F4ED]" data-testid="report-viewer-page">
      <div className="container-phs py-10 lg:py-14">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <p className="eyebrow">Quote Review Report</p>
            <h1 className="font-display text-2xl sm:text-3xl text-[#071A33] mt-0.5">Independent Technical Review</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copyLink} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="report-copy-btn"><Copy size={14}/> Copy link</button>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-primary h-10 py-0 px-4 text-sm" data-testid="report-pdf-btn"><Download size={14}/> Download PDF</a>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Meta */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="card-default" data-testid="report-meta">
              <p className="eyebrow">Case</p>
              <h3 className="font-display text-xl text-[#071A33] mt-1">{c.full_name}</h3>
              <p className="text-sm text-[#475569] mt-0.5">Ref: {String(c.id || "").slice(0, 8).toUpperCase()}</p>
              <div className="mt-4 space-y-2 text-sm">
                <Meta k="Park / plot"   v={`${c.park_name || "—"} · Plot ${c.plot_number || "—"}`}/>
                <Meta k="Location"      v={c.town_location}/>
                <Meta k="Type of works" v={c.type_of_works}/>
                <Meta k="Contractor"    v={c.contractor_name}/>
                <Meta k="Quote value"   v={c.quote_value ? `£${Number(c.quote_value).toLocaleString()}` : "—"}/>
                {c.member_discount_enabled && c.member_discount_amount > 0 && (
                  <div className="flex items-center justify-between py-1.5 border-b border-[#E2DCD0]" data-testid="report-member-saving-row">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#064E2E] font-semibold">Member saving</span>
                    <span className="text-sm font-semibold text-[#B88A2A] tabular-nums">
                      −£{Number(c.member_discount_amount).toLocaleString()} <span className="text-[10px] text-[#475569] font-normal">({c.member_discount_percentage}% off)</span>
                    </span>
                  </div>
                )}
                <Meta k="Main concern"  v={c.main_concern}/>
                <Meta k="Reviewed by"   v={r.reviewer_name || "Park Home Support"}/>
                <Meta k="Report date"   v={(r.published_at || r.updated_at || "").slice(0,10)}/>
              </div>
            </div>

            <div className="card-dark" data-testid="report-member-upsell">
              <p className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs font-medium">Next time — protect yourself</p>
              <p className="font-display text-xl mt-2">Join Park Home Support</p>
              <p className="text-sm text-white/80 mt-2">Priority quote review, reduced inspection rates, ongoing support for 12 months — £99 first year, then £249/yr.</p>
              <Link to="/membership" className="btn-accent mt-4 w-full" data-testid="report-join-btn">Join for £99 <ArrowRight size={14}/></Link>
            </div>
          </aside>

          {/* Report body */}
          <main className="lg:col-span-8 space-y-5">
            {/* Verdict band */}
            <div className={`${vs.bg} text-white rounded-2xl p-6 border-t-4 border-[#B88A2A]`} data-testid="report-verdict-band">
              <p className="text-xs tracking-[0.22em] uppercase font-medium opacity-80">Verdict</p>
              <p className="font-display text-2xl sm:text-3xl mt-1 leading-tight">{vs.label}</p>
              {r.verdict_summary && <p className="mt-3 text-white/90 leading-relaxed">{r.verdict_summary}</p>}
              <p className="mt-4 text-[#FFE7B6] italic text-sm" data-testid="report-recovery-line">
                Most members recover the cost of the plan on their first issue.
              </p>
            </div>

            {c.member_discount_enabled && c.member_discount_amount > 0 && (
              <div
                className="rounded-2xl border border-[#B88A2A] bg-[#FDF6E3] p-5 sm:p-6 relative overflow-hidden"
                data-testid="report-member-saving-callout"
              >
                <span className="absolute inset-y-0 left-0 w-1.5 bg-[#B88A2A]"/>
                <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#064E2E] font-semibold">Member saving</p>
                <p className="font-display text-2xl sm:text-3xl text-[#071A33] mt-1 leading-tight">
                  You saved <span className="text-[#B88A2A]">£{Number(c.member_discount_amount).toLocaleString()}</span> on this quote.
                </p>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">
                  As a Park Home Support member you receive {c.member_discount_percentage}% off our work price.
                  {c.client_price_before_discount > 0 && (
                    <> Pre-member price: <span className="text-[#071A33] font-medium">£{Number(c.client_price_before_discount).toLocaleString()}</span>. Your price: <span className="text-[#071A33] font-medium">£{Number(c.client_price_ex_vat).toLocaleString()}</span> (ex VAT).</>
                  )}
                </p>
              </div>
            )}

            <ReportSection n="1" title="Scope analysis" body={r.scope_analysis} testid="section-scope"/>
            <ReportSection n="2" title="Cost assessment" body={r.cost_assessment} testid="section-cost"/>

            {/* Risk flags */}
            <section className="card-default" data-testid="section-risks">
              <p className="eyebrow">Section 3</p>
              <h2 className="font-display text-2xl text-[#071A33] mt-1">Risk flags</h2>
              {(!r.risk_flags || r.risk_flags.length === 0) ? (
                <p className="text-[#475569] mt-3">No specific risks flagged.</p>
              ) : (
                <ul className="mt-4 space-y-2.5">
                  {r.risk_flags.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`shrink-0 ${RISK_COLOUR[f.level] || RISK_COLOUR.amber} text-white text-[10px] tracking-[0.18em] font-medium uppercase px-2.5 py-1 rounded-full`}>
                        {(f.level || "amber")}
                      </span>
                      <span className="text-[#071A33] leading-relaxed">{f.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <ReportSection n="4" title="Insurance considerations" body={r.insurance_considerations} testid="section-insurance" icon={<ShieldAlert size={18} className="text-[#0F766E]"/>} />
            <ReportSection n="5" title="Recommended next step" body={r.next_step} testid="section-next" icon={<CheckCircle2 size={18} className="text-[#064E2E]"/>} />

            {/* What You Do Next Matters — conversion block */}
            <section className="card-default" data-testid="next-matters-block">
              <p className="eyebrow">Action required</p>
              <h2 className="font-display text-2xl sm:text-3xl text-[#071A33] mt-1">What You Do Next Matters</h2>
              <p className="mt-3 text-[#334155] leading-relaxed">
                Based on our review, proceeding without further checks could result in unnecessary cost or unresolved issues. Choose a path below.
              </p>

              <div className="mt-6 grid md:grid-cols-3 gap-4">
                {/* Option 1 — Membership */}
                <div className="rounded-2xl border-2 border-[#064E2E] bg-[#064E2E]/5 p-5 flex flex-col" data-testid="option-membership">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-[#064E2E] text-white text-sm font-bold flex items-center justify-center">1</span>
                    <span className="eyebrow text-[#064E2E]">Recommended</span>
                  </div>
                  <h3 className="font-display text-lg text-[#071A33] mt-3">Continue with Support (Membership)</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#334155]">
                    <li>• Ongoing guidance on this issue</li>
                    <li>• Help dealing with the contractor</li>
                    <li>• Further review of any revised quote</li>
                  </ul>
                  <Link
                    to={`/membership?source=report&token=${token}`}
                    className="btn-primary mt-4 w-full text-sm"
                    data-testid="next-option-membership-btn"
                  >
                    Continue With Full Support <ArrowRight size={14}/>
                  </Link>
                </div>

                {/* Option 2 — Inspection */}
                <div className="rounded-2xl border border-[#B88A2A] bg-[#FFF8EC] p-5 flex flex-col" data-testid="option-inspection">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-[#B88A2A] text-white text-sm font-bold flex items-center justify-center">2</span>
                    <span className="eyebrow text-[#B88A2A]">Validate</span>
                  </div>
                  <h3 className="font-display text-lg text-[#071A33] mt-3">Request an Inspection</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#334155]">
                    <li>• Confirm cause</li>
                    <li>• Validate scope</li>
                    <li>• Strengthen your position before work starts</li>
                  </ul>
                  <Link
                    to={`/request-support?source=report&token=${token}&type=inspection`}
                    className="btn-accent mt-4 w-full text-sm"
                    data-testid="next-option-inspection-btn"
                  >
                    Request an Inspection <ArrowRight size={14}/>
                  </Link>
                </div>

                {/* Option 3 — Proceed at own risk */}
                <div className="rounded-2xl border border-[#D7E7DF] bg-white p-5 flex flex-col" data-testid="option-independent">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-[#475569] text-white text-sm font-bold flex items-center justify-center">3</span>
                    <span className="eyebrow text-[#475569]">At your own risk</span>
                  </div>
                  <h3 className="font-display text-lg text-[#071A33] mt-3">Proceed Independently</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#334155]">
                    <li>• No further support from us</li>
                    <li>• You accept the risks flagged above</li>
                    <li>• You can return to us at any time</li>
                  </ul>
                  <Link
                    to="/"
                    className="btn-outline mt-4 w-full text-sm"
                    data-testid="next-option-independent-btn"
                  >
                    Proceed at Your Own Risk
                  </Link>
                </div>
              </div>
            </section>
            <div className="card-default border-l-4 border-[#B88A2A] bg-[#FFF8EC]" data-testid="report-disclaimer">
              <div className="flex gap-3 items-start">
                <Info className="text-[#B88A2A] mt-0.5 shrink-0" size={20}/>
                <p className="text-sm text-[#3F2E10] leading-relaxed">
                  <strong>How to use this report.</strong> You may forward this document to the contractor, your insurer, or a loss adjuster.
                  Park Home Support is not insurance and is not a warranty. Our guidance is non-regulated. Final decisions on whether to proceed, pay, claim or settle remain with you, your insurer or your contractor.
                </p>
              </div>
            </div>

            <p className="text-sm text-[#64748B] mt-2 italic" data-testid="report-trust-line">
              This report is based on real-world assessment of similar park home repair and insurance-related issues.
            </p>

            {/* Share block + leave-review CTA */}
            <ShareBlock
              refCode={c?.id || ""}
              className="mt-6"
              data-testid="report-share-block"
            />

            <div className="card-default mt-4" data-testid="report-leave-review-cta">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <h3 className="font-display text-xl text-[#071A33] leading-tight">Was this helpful?</h3>
                  <p className="mt-2 text-[#475569] text-[15px]">
                    If this report saved you from getting something wrong, a quick line of feedback helps other owners trust the process.
                  </p>
                </div>
                <Link
                  to={`/leave-review?source=${encodeURIComponent(c?.id || "")}`}
                  className="btn-outline shrink-0"
                  data-testid="report-leave-review-btn"
                >
                  Leave Feedback <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function Meta({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex justify-between gap-3 border-b border-[#E2DCD0] pb-2 last:border-0 last:pb-0">
      <span className="text-[#475569]">{k}</span>
      <span className="text-[#071A33] font-medium text-right">{v}</span>
    </div>
  );
}

function ReportSection({ n, title, body, testid, icon }) {
  return (
    <section className="card-default" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <p className="eyebrow">Section {n}</p>
      </div>
      <h2 className="font-display text-2xl text-[#071A33] mt-1">{title}</h2>
      <p className="mt-3 text-[#071A33] leading-relaxed whitespace-pre-wrap">{body || "—"}</p>
    </section>
  );
}
